import { useState, useEffect } from 'react';
import { formatCurrency, parseAmount, formatAmountInput } from '../../utils/formatting';
import { calcTotalDeductions, calcLifeInsuranceTotal, calcEarthquakeDeduction, calcLifeInsuranceCategory, calcLifeInsuranceNew } from '../../utils/tax';

export const Deductions = ({ showAlert, currentFiscalYear, fileSystem }) => {
    const [deductions, setDeductions] = useState({
        socialInsurance: { nationalHealth: 0, nationalPension: 0, pensionFund: 0 },
        smallBusiness: { kyosai: 0, ideco: 0 },
        lifeInsurance: {
            general: { newContract: 0, oldContract: 0 },
            caregiving: { newContract: 0 },
            pension: { newContract: 0, oldContract: 0 }
        },
        earthquake: { earthquake: 0, oldLongTerm: 0 }
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (fileSystem && currentFiscalYear) {
            setIsLoading(true);
            fileSystem.getDeductions(currentFiscalYear).then(data => {
                if (data) {
                    setDeductions(data);
                }
            }).catch(err => {
                console.error('Failed to load deductions:', err);
            }).finally(() => {
                setIsLoading(false);
            });
        }
    }, [fileSystem, currentFiscalYear]);

    const updateValue = (category, field, value) => {
        setDeductions(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [field]: parseInt(value) || 0
            }
        }));
    };

    const updateNestedValue = (category, subCategory, field, value) => {
        setDeductions(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [subCategory]: {
                    ...prev[category]?.[subCategory],
                    [field]: parseInt(value) || 0
                }
            }
        }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const success = await fileSystem.saveDeductions(currentFiscalYear, deductions);
            if (success) {
                showAlert('所得控除データを保存しました');
            } else {
                showAlert('保存に失敗しました', 'error');
            }
        } catch (err) {
            console.error('Failed to save deductions:', err);
            showAlert('保存に失敗しました', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const totalDeductions = calcTotalDeductions(deductions);
    const lifeInsuranceTotal = calcLifeInsuranceTotal(deductions.lifeInsurance || {});
    const generalDeduction = calcLifeInsuranceCategory(
        deductions.lifeInsurance?.general?.newContract || 0,
        deductions.lifeInsurance?.general?.oldContract || 0
    );
    const caregivingDeduction = calcLifeInsuranceNew(deductions.lifeInsurance?.caregiving?.newContract || 0);
    const pensionDeduction = calcLifeInsuranceCategory(
        deductions.lifeInsurance?.pension?.newContract || 0,
        deductions.lifeInsurance?.pension?.oldContract || 0
    );
    const lifeInsuranceRaw = generalDeduction + caregivingDeduction + pensionDeduction;
    const earthquakeTotal = calcEarthquakeDeduction(
        deductions.earthquake?.earthquake || 0,
        deductions.earthquake?.oldLongTerm || 0
    );

    if (isLoading) {
        return (
            <div className="page-container">
                <h2>所得控除 <span className="year-badge">{currentFiscalYear}年</span></h2>
                <p style={{ textAlign: 'center', padding: '40px' }}>読み込み中...</p>
            </div>
        );
    }

    return (
        <div className="page-container">
            <h2>所得控除 <span className="year-badge">{currentFiscalYear}年</span></h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
                確定申告書B（第一表）の所得控除欄に記載する金額を入力してください。
            </p>

            <div className="card">
                <h3 className="card-title">社会保険料控除（全額控除）</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div className="form-group">
                        <label>国民健康保険</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.socialInsurance?.nationalHealth || 0)}
                            onChange={e => updateValue('socialInsurance', 'nationalHealth', parseAmount(e.target.value))}
                        />
                    </div>
                    <div className="form-group">
                        <label>国民年金</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.socialInsurance?.nationalPension || 0)}
                            onChange={e => updateValue('socialInsurance', 'nationalPension', parseAmount(e.target.value))}
                        />
                    </div>
                    <div className="form-group">
                        <label>国民年金基金</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.socialInsurance?.pensionFund || 0)}
                            onChange={e => updateValue('socialInsurance', 'pensionFund', parseAmount(e.target.value))}
                        />
                    </div>
                </div>
            </div>

            <div className="card">
                <h3 className="card-title">小規模企業共済等掛金控除（全額控除）</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div className="form-group">
                        <label>小規模企業共済</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.smallBusiness?.kyosai || 0)}
                            onChange={e => updateValue('smallBusiness', 'kyosai', parseAmount(e.target.value))}
                        />
                    </div>
                    <div className="form-group">
                        <label>iDeCo（個人型確定拠出年金）</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.smallBusiness?.ideco || 0)}
                            onChange={e => updateValue('smallBusiness', 'ideco', parseAmount(e.target.value))}
                        />
                    </div>
                </div>
            </div>

            <div className="card">
                <h3 className="card-title">生命保険料控除（控除額: {formatCurrency(lifeInsuranceTotal)}）</h3>
                <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
                    支払った保険料の金額を入力してください。控除額は自動計算されます（上限12万円）
                </p>

                <h4 style={{ fontSize: '14px', marginBottom: '12px' }}>一般生命保険料</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                    <div className="form-group">
                        <label>新契約（H24.1.1以後）</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.lifeInsurance?.general?.newContract || 0)}
                            onChange={e => updateNestedValue('lifeInsurance', 'general', 'newContract', parseAmount(e.target.value))}
                        />
                    </div>
                    <div className="form-group">
                        <label>旧契約（H23.12.31以前）</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.lifeInsurance?.general?.oldContract || 0)}
                            onChange={e => updateNestedValue('lifeInsurance', 'general', 'oldContract', parseAmount(e.target.value))}
                        />
                    </div>
                </div>

                <h4 style={{ fontSize: '14px', marginBottom: '12px' }}>介護医療保険料</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                    <div className="form-group">
                        <label>新契約（H24.1.1以後）</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.lifeInsurance?.caregiving?.newContract || 0)}
                            onChange={e => updateNestedValue('lifeInsurance', 'caregiving', 'newContract', parseAmount(e.target.value))}
                        />
                    </div>
                </div>

                <h4 style={{ fontSize: '14px', marginBottom: '12px' }}>個人年金保険料</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div className="form-group">
                        <label>新契約（H24.1.1以後）</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.lifeInsurance?.pension?.newContract || 0)}
                            onChange={e => updateNestedValue('lifeInsurance', 'pension', 'newContract', parseAmount(e.target.value))}
                        />
                    </div>
                    <div className="form-group">
                        <label>旧契約（H23.12.31以前）</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.lifeInsurance?.pension?.oldContract || 0)}
                            onChange={e => updateNestedValue('lifeInsurance', 'pension', 'oldContract', parseAmount(e.target.value))}
                        />
                    </div>
                </div>

                {lifeInsuranceRaw > 120_000 && (
                    <div style={{marginTop: '12px', padding: '10px 14px', background: '#fff3e0', border: '1px solid #ff9800', borderRadius: '6px', fontSize: '13px', color: '#e65100'}}>
                        各カテゴリの控除額合計（{formatCurrency(lifeInsuranceRaw)}円）が12万円を超えています。控除額は12万円に制限されます。
                    </div>
                )}
                <div style={{marginTop: '12px', padding: '8px 14px', background: '#f5f5f5', borderRadius: '6px', fontSize: '14px', fontWeight: 'bold'}}>
                    生命保険料控除額: {formatCurrency(lifeInsuranceTotal)} 円
                </div>
            </div>

            <div className="card">
                <h3 className="card-title">地震保険料控除（控除額: {formatCurrency(earthquakeTotal)}）</h3>
                <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
                    支払った保険料の金額を入力してください。控除額は自動計算されます（上限5万円）
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div className="form-group">
                        <label>地震保険料</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.earthquake?.earthquake || 0)}
                            onChange={e => updateValue('earthquake', 'earthquake', parseAmount(e.target.value))}
                        />
                    </div>
                    <div className="form-group">
                        <label>旧長期損害保険料</label>
                        <input
                            type="text"
                            value={formatAmountInput(deductions.earthquake?.oldLongTerm || 0)}
                            onChange={e => updateValue('earthquake', 'oldLongTerm', parseAmount(e.target.value))}
                        />
                    </div>
                </div>
            </div>

            <div className="card" style={{ background: '#e8f5e9' }}>
                <h3 className="card-title">所得控除合計</h3>
                <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#2e7d32', marginBottom: '20px' }}>
                    {formatCurrency(totalDeductions)}
                </p>
                <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? '保存中...' : '保存する'}
                </button>
            </div>
        </div>
    );
};

export default Deductions;
